import { process, State } from '@progress/kendo-data-query';
import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridRowProps,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { cloneElement, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';

import {
  useInvoiceDetailsLineItems,
  useInvoiceDetailsLineItemsSubGrid,
} from 'src/assets/static/invoice_details/LineItems';
import Accordion from 'src/components/design-system/Accordion';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import GridWrapper from 'src/components/GridWrapper';
import useScrollDown from 'src/hooks/useScrollDown';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { useContextSelector } from 'use-context-selector';
import AddLineItem from './AddLineItem';
import BulkAddLineItems from './BulkAddLineItems';
import BulkEditLineItems from './BulkEditLineItems';
import CombineLineItems from './CombineLineItems';
import DeleteLineItem from './DeleteLineItem';
import { InvoiceContext } from './InvoiceDetailsContext';
import SplitLineItems from './SplitLineItems';

const initialLineItemsDataState: State = {};

const defaultProps = {
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  pageable: false,
  showColumnMenu: true,
  tableTextId: 'Line Items',
};

const InvoiceLineItems = () => {
  const lineItemsWithErrorWarningStatus = useContextSelector(
    InvoiceContext,
    (value) => value.lineItemsWithErrorWarningStatus
  );
  const setInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.setInvoiceLineItems
  );
  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const setSelectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.setSelectedLineItemIds
  );
  const setExpandedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.setExpandedLineItemIds
  );

  const setIsLineItemClick = useContextSelector(
    InvoiceContext,
    (value) => value.setIsLineItemClick
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const showScans = useContextSelector(InvoiceContext, (value) => value.showScans);

  const isLineItemClick = useContextSelector(InvoiceContext, (value) => value.isLineItemClick);

  const [dataState, setDataState] = useState(initialLineItemsDataState);
  const subGridColumns = useInvoiceDetailsLineItemsSubGrid();
  const { data, total } = useMemo(
    () =>
      process(lineItemsWithErrorWarningStatus || [], dataState) as {
        data: InvoiceLineItem[];
        total: number;
      },
    [dataState, lineItemsWithErrorWarningStatus]
  );
  const columns = useInvoiceDetailsLineItems();

  const handleDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => setDataState(e.dataState),
    []
  );

  const clearFilters = useCallback(() => setDataState(initialLineItemsDataState), []);

  const handleItemChange = useCallback(
    (e: GridItemChangeEvent) => {
      const { field = '', value } = e;
      const { rowId, receiptLineItemId } = e.dataItem;

      if (field === 'expanded') {
        setExpandedLineItemIds((prev) =>
          prev.includes(receiptLineItemId)
            ? prev.filter((item) => item !== receiptLineItemId)
            : [...prev, receiptLineItemId]
        );
        return;
      }

      setInvoiceLineItems((prev) => {
        let prevData = [...prev];
        const index = prevData.findIndex((lineItem) => lineItem.rowId === rowId);
        const item = prevData[index];
        prevData[index] = {
          ...item,
          [field]: value,

          ...(field === 'priceAmt'
            ? {
                subTotal: (value || 0) * (Number(item?.quantityReceived) || 0),
              }
            : field === 'quantityReceived'
            ? {
                subTotal: (item?.priceAmt || 0) * (value || 0),
                completed: !(item?.poQty > item?.previousQty + value || 0) ? 'Y' : 'N',
              }
            : {}),
        };

        return prevData;
      });
    },
    [setExpandedLineItemIds, setInvoiceLineItems]
  );

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setSelectedLineItemIds((prev) =>
        prev.includes(event.dataItem.receiptLineItemId)
          ? prev.filter((item) => item !== event.dataItem.receiptLineItemId)
          : [...prev, event.dataItem.receiptLineItemId]
      );
    },
    [setSelectedLineItemIds]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const { checked } = event.syntheticEvent.target as any;
      if (checked) {
        setSelectedLineItemIds(lineItemsWithErrorWarningStatus.map((v) => v.receiptLineItemId));
      } else {
        setSelectedLineItemIds([]);
      }
    },
    [lineItemsWithErrorWarningStatus, setSelectedLineItemIds]
  );

  const DetailComponent = useCallback(
    (props: GridDetailRowProps) => (
      <Grid data={[props?.dataItem]}>
        {subGridColumns.map((column) => (
          <GridColumn
            key={column.field}
            field={column?.field?.toString()}
            title={column?.title?.toString()}
            width={column.width}
            format={column.format?.toString()}
          />
        ))}
      </Grid>
    ),
    [subGridColumns]
  );

  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
    const style = { backgroundColor: '#ddd' };
    let trProps: any = {};
    if (props?.isAltRow) {
      trProps.style = style;
    }
    return cloneElement(trElement, { ...trProps });
  };

  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  const tableRef = useScrollDown(isLineItemClick, setIsLineItemClick, [data]);

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      const ocrActiveInputElement = (document.activeElement as HTMLInputElement).getAttribute(
        'ocr-data'
      );
      const isOcrBadgeElement =
        (event.target instanceof HTMLElement &&
          event.target.getAttribute('data-tooltip-id')?.includes('ocr-tooltip')) ||
        false;

      if (ocrActiveInputElement && isOcrBadgeElement) {
        const data = ocrActiveInputElement.split(':');
        const field = data[0];
        const parsed =
          data.length === 2
            ? data[1]
                .split(',')
                .map(Number)
                .filter((n) => !isNaN(n))
            : [];
        const index = parsed.length > 0 ? parsed : undefined;

        handleHoverOcrField(field, index);
      }
    };

    window.addEventListener('mouseleave', handleMouseLeave, true);

    return () => {
      window.removeEventListener('mouseleave', handleMouseLeave, true);
    };
  }, [handleHoverOcrField]);

  return (
    <Accordion title={`Line Items for ${title}`} defaultExpanded>
      <GridWrapper
        id="invoiceLineItemGrid"
        key={`${showScans}`}
        rowRender={rowRender}
        columns={columns}
        dataSource={data || []}
        dataState={dataState}
        total={total}
        gridToolbarChildren={
          <InvoiceLineItemsToolbar
            clearFilters={clearFilters}
            isClearFiltersDisabled={isClearFiltersDisabled}
          />
        }
        onDataStateChange={handleDataStateChange}
        onItemChange={handleItemChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
        onSelectionChange={onSelectionChange}
        detail={DetailComponent}
        {...defaultProps}
        className={'max-h-[400px] mb-5'}
        isCustomExpandableRow
        ref={tableRef}
      />
    </Accordion>
  );
};

export default memo(InvoiceLineItems);

interface InvoiceLineItemsToolbarProps {
  clearFilters: VoidFunction;
  isClearFiltersDisabled: boolean;
}

const InvoiceLineItemsToolbar: FC<InvoiceLineItemsToolbarProps> = memo(
  ({ clearFilters, isClearFiltersDisabled }) => {
    const isInvoiceEditDisabled = useContextSelector(
      InvoiceContext,
      (value) => value.isInvoiceEditDisabled
    );

    return (
      <div className="flex justify-center items-center flex-wrap">
        <ClearFilterButton disabled={isClearFiltersDisabled} onClick={clearFilters} />
        {!isInvoiceEditDisabled ? (
          <>
            <SplitLineItems />
            <CombineLineItems />
            <AddLineItem />
            <BulkAddLineItems />
            <BulkEditLineItems />
            <DeleteLineItem />
          </>
        ) : (
          ''
        )}
      </div>
    );
  }
);
